import React, { useState, useEffect } from 'react'
import { Popup } from 'semantic-ui-react'
import { filters, FilterSchema } from '@adc/blueprint'
import FilterButton from '../filterButton'
import FilterPanel from '../filterPanel'
import FilterGroupLayout from '../filterGroupLayout'
import { fullScreenModifier } from './fullScreenModifier'
import classnames from 'classnames/bind'
import styles from './styles.scss'
import type { ButtonWidth } from '../types'
const cx = classnames.bind(styles)

/**
 * NOTE TO ROMAN (and I'm speaking to you as you'll probably be implementing this...)
 * This is a mobile filter component that renders all the filter groups.
 * It's primarily built for SRP mobile use but could be used for other mobile use cases in the future.
 *
 * It DOES NOT yet account for the "sort" aspect of the current filter - because for one I'm not sure it
 * makes sense here.  If we do decide to add sort, we'll need to discuss how to handle that (perhaps a slot).
 * Point being, do not come in here and add sort as that would not be generic for this component.
 */

type MobileFilterProps = {
  defaultButtonLabel?: string // optional hard-coded label
  definitions: FilterSchema.FilterGroupDefinitionArray
  dataCollection: FilterSchema.FilterGroupViewModelCollection
  buttonWidth?: ButtonWidth
  onChange: (
    definitionName: string,
    key: string,
    value: FilterSchema.ValueType
  ) => void
  onReset: () => void
  elmId?: string
}

/**
 * Filter: a filter component that renders ALL the filter groups
 *
 * @definition group definition
 * @data group data
 *
 * @returns
 */
const MobileFilter: React.FunctionComponent<MobileFilterProps> = ({
  defaultButtonLabel = 'Filter',
  definitions,
  dataCollection,
  buttonWidth,
  onChange,
  onReset,
  elmId = 'filter',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isFilterSelected, setIsFilterSelected] = useState(false) // as in, at least one filter is in use
  const [filterNotSelectedButtonLabel, setFilterNotSelectedButtonLabel] =
    useState(defaultButtonLabel)

  const [buttonLabel, setButtonLabel] = useState(filterNotSelectedButtonLabel)

  useEffect(() => {
    setIsFilterSelected(!filters.utils.areAllFiltersReset(dataCollection))
    setButtonLabel(filterNotSelectedButtonLabel)
  }, [dataCollection, filterNotSelectedButtonLabel])

  useEffect(() => {
    setFilterNotSelectedButtonLabel(defaultButtonLabel)
  }, [defaultButtonLabel])

  useEffect(() => {
    setButtonLabel(
      filters.utils.getLabelFromAll(
        dataCollection,
        definitions,
        defaultButtonLabel
      )
    )
  }, [dataCollection, defaultButtonLabel, definitions])

  return (
    <Popup
      content={
        <FilterPanel
          elmId={`${elmId}_panel`}
          onOpen={setIsOpen}
          onReset={onReset}
        >
          {definitions.map(definition => {
            const data = dataCollection[definition.name]
            return (
              <FilterGroupLayout
                key={definition.name}
                groupDefinition={definition}
                groupData={data}
                onChange={(key, value) => {
                  onChange(definition.name, key, value)
                }}
              />
            )
          })}
        </FilterPanel>
      }
      on='click'
      open={isOpen}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      className={cx('filter-popup')}
      trigger={
        <FilterButton
          isFilterSelected={isFilterSelected}
          onOpen={setIsOpen}
          isOpen={isOpen}
          onReset={() => onReset()}
          label={buttonLabel}
          width={buttonWidth}
          elmId={`${elmId}_button`}
        />
      }
      position='bottom left'
      popperModifiers={[fullScreenModifier]}
    />
  )
}

export default MobileFilter
